/**
 * End Coords Gizmo component for interactive end effector position control
 * Uses PivotControls from @react-three/drei for both position and rotation control
 * Supports snapping to clean values for position and rotation
 */

import { useRef, useEffect, useCallback, useState } from "react";
import { PivotControls } from "@react-three/drei";
import * as THREE from "three";
import { useWebSocket } from "../WebSocket";
import {
  useEndCoordsGizmoPosition,
  useEndCoordsGizmoQuaternion,
  useEndCoordsSnapEnabled,
  useEndCoordsPositionSnap,
  useEndCoordsRotationSnap,
} from "../store";

interface EndCoordsGizmoProps {
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

/**
 * Snap a value to the nearest multiple of step
 */
function snapValue(value: number, step: number): number {
  return Math.round(value / step) * step;
}

/**
 * Snap position to grid
 */
function snapPosition(
  pos: THREE.Vector3,
  step: number
): THREE.Vector3 {
  return new THREE.Vector3(
    snapValue(pos.x, step),
    snapValue(pos.y, step),
    snapValue(pos.z, step)
  );
}

/**
 * Snap quaternion rotation to nearest angle step
 * Converts to Euler, snaps each axis, then converts back
 */
function snapRotation(
  quat: THREE.Quaternion,
  step: number
): THREE.Quaternion {
  const euler = new THREE.Euler().setFromQuaternion(quat, "ZYX");
  const snappedEuler = new THREE.Euler(
    snapValue(euler.x, step),
    snapValue(euler.y, step),
    snapValue(euler.z, step),
    "ZYX"
  );
  return new THREE.Quaternion().setFromEuler(snappedEuler);
}

export function EndCoordsGizmo({ onDragStart, onDragEnd }: EndCoordsGizmoProps) {
  const { sendEndCoordsGizmoUpdate } = useWebSocket();
  const isDraggingRef = useRef(false);
  const position = useEndCoordsGizmoPosition();
  const quaternion = useEndCoordsGizmoQuaternion();
  const snapEnabled = useEndCoordsSnapEnabled();
  const positionSnap = useEndCoordsPositionSnap();
  const rotationSnap = useEndCoordsRotationSnap();

  // Store current position/rotation as state for PivotControls matrix
  const [matrix, setMatrix] = useState<THREE.Matrix4>(() => {
    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3(...position);
    const [x, y, z, w] = quaternion;
    const quat = new THREE.Quaternion(x, y, z, w);
    m.compose(pos, quat, new THREE.Vector3(1, 1, 1));
    return m;
  });

  // Update matrix from store data (when backend updates position)
  useEffect(() => {
    // Only update if not currently dragging
    if (isDraggingRef.current) return;

    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3(...position);
    const [x, y, z, w] = quaternion;
    const quat = new THREE.Quaternion(x, y, z, w);
    m.compose(pos, quat, new THREE.Vector3(1, 1, 1));
    setMatrix(m);
  }, [position, quaternion]);

  // Handle drag - apply snapping and send to backend
  const handleDrag = useCallback(
    (localMatrix: THREE.Matrix4) => {
      if (!isDraggingRef.current) return;

      let pos = new THREE.Vector3();
      let quat = new THREE.Quaternion();
      const scale = new THREE.Vector3();
      localMatrix.decompose(pos, quat, scale);

      // Apply snapping if enabled
      if (snapEnabled) {
        pos = snapPosition(pos, positionSnap);
        quat = snapRotation(quat, rotationSnap);

        // Update the matrix with snapped values for visual feedback
        const snappedMatrix = new THREE.Matrix4();
        snappedMatrix.compose(pos, quat, scale);
        setMatrix(snappedMatrix);
      }

      const posArray: [number, number, number] = [pos.x, pos.y, pos.z];
      const quatArray: [number, number, number, number] = [
        quat.x,
        quat.y,
        quat.z,
        quat.w,
      ];

      sendEndCoordsGizmoUpdate(posArray, quatArray);
    },
    [sendEndCoordsGizmoUpdate, snapEnabled, positionSnap, rotationSnap]
  );

  return (
    <PivotControls
      matrix={matrix}
      scale={0.1}
      lineWidth={2}
      fixed={false}
      activeAxes={[true, true, true]}
      disableAxes={false}
      disableSliders={true}
      disableRotations={false}
      disableScaling={true}
      depthTest={false}
      opacity={0.8}
      onDragStart={() => {
        isDraggingRef.current = true;
        onDragStart?.();
      }}
      onDrag={(localMatrix) => {
        handleDrag(localMatrix);
      }}
      onDragEnd={() => {
        isDraggingRef.current = false;
        onDragEnd?.();
      }}
    />
  );
}
